import { useEffect, useRef } from 'react';

/**
 * Fires `track` exactly once per mount, as soon as `ready` becomes true.
 *
 * Several onboarding steps (Express and Control) derive their tracking payload from data
 * that resolves asynchronously (feature flags, todo items, session state, view toggles).
 * Keying the effect on those values directly causes the "*:Viewed" event to fire once per
 * value change instead of once per step view, which double-counts steps and skews
 * step-to-step funnel comparisons.
 *
 * The `fired` ref guarantees the callback runs at most once per mount: once it has fired,
 * later renders (even ones that produce a new `track` closure) just re-run this effect and
 * hit the early return.
 */
export function useTrackViewedOnce(ready: boolean, track: () => void) {
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current || !ready) return;
    fired.current = true;
    track();
  }, [ready, track]);
}
